<template>
  <div v-if="notifications.length" class="alert-region" role="region" aria-label="Notiser">
    <div class="alert-list">
      <article
        v-for="notification in notifications"
        :key="notification.id"
        class="alert"
        :type="notification.type"
        role="status"
      >
        <div class="alert-icon" aria-hidden="true">
          <SvgIcon :path="iconPaths[notification.type]" :size="20" />
        </div>

        <div class="alert-content">
          <h2>{{ notification.title }}</h2>
          <p v-if="notification.description">
            {{ notification.description }}
          </p>
        </div>

        <button
          v-if="notification.dismissable"
          class="alert-dismiss"
          type="button"
          aria-label="Stäng notis"
          @click="dismissNotification(notification.id)"
        >
          <SvgIcon :path="mdiClose" :size="18" />
        </button>
      </article>
    </div>
  </div>
</template>

<script setup lang="ts">
import { mdiAlertCircleOutline, mdiCheckCircleOutline, mdiClose, mdiInformationOutline } from '@mdi/js'

import SvgIcon from '../SvgIcon/SvgIcon.vue'
import { dismissNotification, useNotifications, type NotificationType } from '../../composables/notifications'

const { notifications } = useNotifications()

const iconPaths: Record<NotificationType, string> = {
  warning: mdiAlertCircleOutline,
  success: mdiCheckCircleOutline,
  error: mdiAlertCircleOutline,
  info: mdiInformationOutline,
}
</script>

<style scoped lang="scss">
.alert-region {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 10000;
  width: min(420px, calc(100vw - 32px));
  pointer-events: none;
}

.alert-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.alert {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 12px;
  align-items: start;
  padding: 14px 14px 14px 16px;
  border: 1px solid var(--alert-border-color);
  border-left-width: 4px;
  border-radius: 6px;
  background-color: var(--alert-background-color);
  color: var(--color-fg-default);
  box-shadow: 0 10px 24px rgba(31, 41, 55, 0.16);
  pointer-events: auto;

  &[type='warning'] {
    --alert-background-color: var(--color-warning-subtle);
    --alert-border-color: var(--color-warning-emphasis);
    --alert-icon-color: var(--color-warning-fg);
  }

  &[type='success'] {
    --alert-background-color: var(--color-success-subtle);
    --alert-border-color: var(--color-success-emphasis);
    --alert-icon-color: var(--color-success-fg);
  }

  &[type='error'] {
    --alert-background-color: var(--color-danger-subtle);
    --alert-border-color: var(--color-danger-emphasis);
    --alert-icon-color: var(--color-danger-fg);
  }

  &[type='info'] {
    --alert-background-color: var(--color-accent-subtle);
    --alert-border-color: var(--color-accent-emphasis);
    --alert-icon-color: var(--color-accent-fg);
  }
}

.alert-icon {
  display: flex;
  color: var(--alert-icon-color);
}

.alert-content {
  min-width: 0;

  h2 {
    margin: 0;
    color: var(--color-fg-default);
    font-size: 14px;
    font-weight: 700;
    line-height: 1.35;
  }

  p {
    margin: 4px 0 0;
    color: var(--color-fg-subtle);
    font-size: 13px;
    line-height: 1.45;
  }
}

.alert-dismiss {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin: -4px -4px 0 0;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background-color: transparent;
  color: var(--color-fg-subtle);
  cursor: pointer;

  &:hover {
    background-color: var(--color-overlay-hover);
    color: var(--color-fg-default);
  }
}
</style>
